import { useState } from 'react';
import { BACKEND_URL } from '@/lib/env';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Play, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['Static Report', 'Code Report', 'Data Migration', 'Interface', 'Enhancement', 'Other'];
const SUB_CATEGORIES = ['ABAP', 'FIORI', 'BASIS', 'SECURITY', 'PI/PO', 'OTHER'];

const DEFAULT_FORM = {
  ticket_number: '',
  title: '',
  category: 'Code Report',
  sub_category: 'ABAP',
  description: '',
  model_id: '',
  attachment: '',
};

export default function TriggerWorkflow() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [response, setResponse] = useState(null);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ticket_number || !form.title || !form.description) {
      toast.error('Ticket number, title, and description are required');
      return;
    }
    setStatus('loading');
    setResponse(null);
    try {
      const payload = {
        ticket_number: form.ticket_number.trim(),
        title: form.title.trim(),
        category: form.category,
        sub_category: form.sub_category || null,
        description: form.description.trim(),
        model_id: form.model_id.trim() || null,
        attachment: form.attachment.trim() || null,
      };
      const res = await axios.post(`${API}/integrations/servicenow/inbound`, payload);
      setStatus('success');
      setResponse(res.data);
      toast.success(`Workflow triggered for ${form.ticket_number}`);
    } catch (err) {
      setStatus('error');
      setResponse(err.response?.data || { message: err.message });
      toast.error('Failed to trigger workflow');
    }
  };

  const handleReset = () => {
    setForm(DEFAULT_FORM);
    setStatus(null);
    setResponse(null);
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Trigger Workflow</h1>
        <p className="text-muted-foreground mt-1">
          Post a ticket directly to the workflow engine via{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">
            POST /api/integrations/servicenow/inbound
          </code>
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Workflow Request</CardTitle>
          <CardDescription>Fill in the ticket details and submit to start a workflow run.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="ticket_number">Ticket Number <span className="text-red-500">*</span></Label>
                <Input
                  id="ticket_number"
                  placeholder="e.g. TEST-POSTMAN-108"
                  value={form.ticket_number}
                  onChange={e => set('ticket_number', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="model_id">Model ID <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input
                  id="model_id"
                  placeholder="Leave blank for default"
                  value={form.model_id}
                  onChange={e => set('model_id', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="title">Title <span className="text-red-500">*</span></Label>
              <Input
                id="title"
                placeholder="e.g. Postman test ticket"
                value={form.title}
                onChange={e => set('title', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => set('category', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Sub Category</Label>
                <Select value={form.sub_category} onValueChange={v => set('sub_category', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUB_CATEGORIES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="description">Description <span className="text-red-500">*</span></Label>
              <Textarea
                id="description"
                placeholder="Describe what the agent should do..."
                rows={5}
                value={form.description}
                onChange={e => set('description', e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="attachment">Attachment <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                id="attachment"
                placeholder="URL or text attachment"
                value={form.attachment}
                onChange={e => set('attachment', e.target.value)}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={status === 'loading'} className="flex items-center gap-2">
                {status === 'loading'
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Play className="w-4 h-4" />}
                {status === 'loading' ? 'Triggering...' : 'Trigger Workflow'}
              </Button>
              <Button type="button" variant="outline" onClick={handleReset}>
                Reset
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {status && status !== 'loading' && (
        <Card className={status === 'success' ? 'border-green-500' : 'border-red-500'}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              {status === 'success'
                ? <CheckCircle className="w-5 h-5 text-green-500" />
                : <XCircle className="w-5 h-5 text-red-500" />}
              {status === 'success' ? 'Workflow Triggered' : 'Error'}
              <Badge variant={status === 'success' ? 'default' : 'destructive'} className="ml-auto">
                {status === 'success' ? '202 Accepted' : 'Failed'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted rounded p-3 overflow-auto whitespace-pre-wrap">
              {JSON.stringify(response, null, 2)}
            </pre>
            {status === 'success' && (
              <p className="text-sm text-muted-foreground mt-2">
                Workflow is running asynchronously. Open ticket{' '}
                <a href={`/tickets/${form.ticket_number}`} className="underline text-primary">
                  {form.ticket_number}
                </a>{' '}
                to track progress.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
